import { AlertController, LoadingController } from '@ionic/angular';
import { AnalyticsService } from '../analytics/analytics.service';
import { Router } from '@angular/router';
import { SignedOutError } from 'src/app/common/errors/signed-out-error';
import { Api } from '../../api/api';
import { TokenExpiredError } from 'src/app/common/errors/token-expired-error';
import { RetryError } from 'src/app/common/errors/retry-error';

export class JobCancelledError extends Error {
  constructor(message = 'Task was cancelled') {
    super(message);
    this.name = 'TaskCancelledError';
  }

  static isInstance(error: unknown): boolean {
    return error instanceof JobCancelledError;
  }
}

export class JobErrorHandler<T> {
  private enableFatalError = false;
  private enableWindowReload = false;
  private static showingWidgetAbort = false;
  private onCancelHandler: (() => void) | null = null;
  private onErrorHandler: ((error: unknown) => Promise<void>) | null = null;

  constructor(
    private analyticsService: AnalyticsService,
    private alertController: AlertController,
    private abortController: AbortController,
    private router: Router,
  ) {}

  async handle(job: () => Promise<T>): Promise<T> {
    try {
      return await job();
    } catch (error) {
      if (!TokenExpiredError.isInstance(error) && !RetryError.isInstance(error)) {
        await this.analyticsService.handleError(error);
      }

      if (!JobCancelledError.isInstance(error) && !this.abortController.signal.aborted) {
        if (SignedOutError.isInstance(error)) {
          if (!JobErrorHandler.showingWidgetAbort) {
            JobErrorHandler.showingWidgetAbort = true;
            await this.showWidgetAbortAlert();
          }
          throw error;
        }

        await this.onErrorHandler?.(error);
        if (this.enableFatalError) {
          await this.showFatalErrorAlert(error);
        }
      }
      this.onCancelHandler?.();

      if (this.enableWindowReload) {
        window.location.reload();
      }

      throw error;
    }
  }

  showFatalError() {
    this.enableFatalError = true;
    return this;
  }

  reloadWindow() {
    this.enableWindowReload = true;
    return this;
  }

  onError(handler: (error: unknown) => Promise<void>) {
    this.onErrorHandler = handler;
    return this;
  }

  onCancel(handler: () => void) {
    this.onCancelHandler = handler;
    return this;
  }

  private async showFatalErrorAlert(error: unknown): Promise<void> {
    const context = error instanceof Error ? `: ${error.message}` : '';
    const alert = await this.alertController.create({
      cssClass: 'alert',
      header: 'Unexpected error',
      message: `An error occurred, try again later. ${context}`,
      buttons: [{ cssClass: 'alert-button', text: 'OK' }],
      backdropDismiss: false,
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  private async showWidgetAbortAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Fatal error!',
      message: 'You will now be logged out.',
      cssClass: 'alert',
      buttons: [
        {
          text: 'OK',
          cssClass: 'alert-button',
          handler: async () => {
            await this.router.navigate(['/']);
            JobErrorHandler.showingWidgetAbort = false;
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
    await alert.onDidDismiss();
  }
}

export class Job<T> {
  private attempt = 0;
  private canRetry?: (attempt: number) => boolean;
  private enableLoading = false;
  private loadingDelay = 1000;
  private abortController = new AbortController();
  private jobErrorHandler?: JobErrorHandler<T>;

  static readonly InfiniteRetry = (_attempt: number) => true;
  static readonly DefaultRetry = (attempt: number) => attempt < 3;

  constructor(
    private task: () => Promise<T>,
    private analyticsService: AnalyticsService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private router: Router,
  ) {}

  async run(): Promise<T> {
    if (this.jobErrorHandler) {
      return await this.jobErrorHandler.handle(() => this.execute());
    }
    return await this.execute();
  }

  async runNoThrow(): Promise<T | undefined> {
    try {
      return await this.run();
    } catch (_error) {
      return undefined;
    }
  }

  addErrorHandler() {
    this.jobErrorHandler = new JobErrorHandler<T>(
      this.analyticsService,
      this.alertController,
      this.abortController,
      this.router,
    );
    return this.jobErrorHandler;
  }

  errorHandler() {
    return this.jobErrorHandler;
  }

  showNoInternetRetry(canRetry: (attempt: number) => boolean = Job.DefaultRetry) {
    this.canRetry = canRetry;
    return this;
  }

  showSpinner(delay = 1000) {
    this.enableLoading = true;
    this.loadingDelay = delay;
    return this;
  }

  cancel() {
    this.abortController.abort();
  }

  private checkIfCancelled() {
    if (this.abortController.signal.aborted) {
      throw new JobCancelledError('Job was cancelled');
    }
  }

  async execute(): Promise<T> {
    if (this.canRetry) {
      return this.handleRetry(async () => {
        return this.enableLoading ? this.handleLoading(this.task) : this.task();
      });
    } else {
      return this.enableLoading ? this.handleLoading(this.task) : this.task();
    }
  }

  private async handleLoading<T>(task: () => Promise<T>): Promise<T> {
    this.checkIfCancelled();
    let loading: HTMLIonLoadingElement | undefined;
    let timeout: NodeJS.Timeout | undefined;
    try {
      loading = await this.loadingController.create({ cssClass: 'custom-loading' });
      timeout = setTimeout(() => {
        if (!this.abortController.signal.aborted) loading?.present();
      }, this.loadingDelay);

      return await task();
    } catch (error) {
      this.checkIfCancelled();
      this.analyticsService.handleError(error);
      throw error;
    } finally {
      clearTimeout(timeout);
      if (loading && loading.isConnected) {
        await loading.dismiss();
      }
    }
  }

  private async handleRetry<T>(job: () => Promise<T>): Promise<T> {
    this.checkIfCancelled();
    try {
      return await job();
    } catch (error) {
      this.checkIfCancelled();
      if (!Api.isNoInternetError(error)) {
        if (Api.isTokenExpired(error)) {
          throw new TokenExpiredError('Token expired');
        }
        await this.analyticsService.handleError(error);
        throw new RetryError('Failed to retry', error);
      }

      const retryAllowed = this.canRetry && this.canRetry(this.attempt);
      this.attempt++;
      this.loadingDelay = 0;

      return new Promise<T>((resolve, reject) => {
        const cancelledError = new JobCancelledError('User cancelled');
        this.alertController
          .create({
            header: 'No Internet',
            message: retryAllowed
              ? 'Please check your connection and try again'
              : 'Please check your connection and try again later',
            buttons: retryAllowed
              ? [
                  {
                    text: 'Cancel',
                    handler: () => reject(cancelledError),
                  },
                  {
                    text: 'Retry',
                    handler: () => {
                      this.alertController.dismiss().then(() => {
                        if (!this.abortController.signal.aborted) {
                          this.handleRetry(job).then(resolve).catch(reject);
                        } else {
                          reject(cancelledError);
                        }
                      });
                    },
                  },
                ]
              : [
                  {
                    text: 'OK',
                    handler: () => reject(cancelledError),
                  },
                ],
            backdropDismiss: false,
          })
          .then((alert) => alert.present());
      });
    }
  }
}
